/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react'
import axios from 'axios';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';
import type { Book } from '../types/book.types';

interface WishlistContextType {
  wishlistItems: Book[];
  addToWishlist: (book: Book) => Promise<void>;
  removeFromWishlist: (bookId: number) => Promise<void>;
  isInWishlist: (bookId: number) => boolean;
  clearWishlist: () => Promise<void>;
  loading: boolean;
  isAddingToWishlist: number | null;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export const WishlistProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [wishlistItems, setWishlistItems] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddingToWishlist, setIsAddingToWishlist] = useState<number | null>(null);

  // Load wishlist from API on component mount or when auth state changes
  useEffect(() => {
    const loadWishlist = async () => {
      if (isAuthenticated) {
        try {
          setLoading(true);
          const token = localStorage.getItem('token');
          const response = await axios.get(`${import.meta.env.VITE_API_URL}/wishlist`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data.status === 'success') {
            setWishlistItems(response.data.data);
          }
        } catch (error) {
          console.error('Failed to fetch wishlist:', error);
          toast.error('فشل في تحميل قائمة المفضلة');
        } finally {
          setLoading(false);
        }
      } else {
        setWishlistItems([]);
        setLoading(false);
      }
    };

    loadWishlist();
  }, [isAuthenticated]);

  const toggleWishlist = async (book: Book) => {
  if (!isAuthenticated) {
    toast.error('يرجى تسجيل الدخول لإضافة الكتب إلى المفضلة');
    return;
  }

  // Get the book ID (handling both direct or nested ID structure)
  const bookId = book.book_id || book.id;
  
  // Check if book is already in wishlist
  const alreadyInWishlist = bookId !== undefined ? isInWishlist(bookId) : false;
  
  if (bookId !== undefined) {
    setIsAddingToWishlist(bookId);
  }


  try {
    const token = localStorage.getItem('token');
    
    if (alreadyInWishlist) {
      // If already in wishlist, remove it
      const response = await axios.delete(`${import.meta.env.VITE_API_URL}/wishlist/${bookId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.status === 'success') {
        setWishlistItems(prev => prev.filter(item => {
          // Handle both book.id and book.book_id cases
          const itemId = item.book_id || item.id;
          return itemId !== bookId;
        }));
        toast.success('تم إزالة الكتاب من المفضلة');
      }
    } else {
      // If not in wishlist, add it
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/wishlist`,
        { bookId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === 'success') {
        // Use the returned item from API which includes all book details
        if (response.data.data) {
          setWishlistItems(prev => [...prev, response.data.data]);
        } else {
          // Fallback in case API doesn't return the added item
          setWishlistItems(prev => [...prev, book]);
        }
        toast.success('تمت إضافة الكتاب إلى المفضلة');
      }
    }
  } catch (error: any) {
    console.error('Error toggling wishlist item:', error);
    toast.error('حدث خطأ أثناء تحديث المفضلة');
  } finally {
    setIsAddingToWishlist(null);
  }
};

  const removeFromWishlist = async (bookId: number) => {
    if (!isAuthenticated) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${import.meta.env.VITE_API_URL}/wishlist/${bookId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.status === 'success') {
        setWishlistItems(prev => prev.filter(item => {
          // Handle both book.id and book.book_id cases
          const itemId = item.book_id || item.id;
          return itemId !== bookId;
        }));
        toast.success('تم إزالة الكتاب من المفضلة');
      }
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      toast.error('حدث خطأ أثناء إزالة الكتاب من المفضلة');
    }
  };

  const clearWishlist = async () => {
    if (!isAuthenticated) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${import.meta.env.VITE_API_URL}/wishlist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.status === 'success') {
        setWishlistItems([]);
        toast.success('تم مسح المفضلة');
      }
    } catch (error) {
      console.error('Error clearing wishlist:', error);
      toast.error('حدث خطأ أثناء مسح المفضلة');
    }
  };

  const isInWishlist = (bookId: number) => {
    return wishlistItems.some(item => {
      // Handle both book.id and book.book_id cases
      const itemId = item.book_id || item.id;
      return itemId === bookId;
    });
  };

  return (
  <WishlistContext.Provider value={{ 
    wishlistItems, 
    addToWishlist: toggleWishlist, // Keep the same name for API compatibility
    removeFromWishlist, 
    isInWishlist,
    clearWishlist,
    loading,
    isAddingToWishlist
  }}>
    {children}
  </WishlistContext.Provider>
);
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (context === undefined) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};